import React, { useEffect, useState } from 'react';
import { useEmployeeNamesService, useEmployeeUrl } from 'domain/shared/employee';
import { generateLinkByEmail } from 'framework/utils';
import { useArtifactInfoService, CROP_SIZES } from 'domain/shared/artifact';
import PersonTileUI, { PersonTileUIProps } from './PersonTileUI/PersonTileUI';

interface PersonTileData {
  id: string | number;
  name?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  position?: string;
  department?: string;
  email?: string;
  employeePhotoId?: string;
  mainPhotoId?: string;
  avatar?: string;
  nameEn?: {
    firstName?: string;
    lastName?: string;
  };
}

export interface EmployeePersonTileProps extends Partial<PersonTileUIProps> {
  employeeId?: string;
  person?: PersonTileData;
  title?: string;
  role?: string;
  showTeamsButton?: boolean;
  teamsUrl?: string;
  withAvatar?: boolean;
}

const EmployeePersonTile: React.FC<EmployeePersonTileProps> = ({
  employeeId,
  person,
  title,
  role,
  showTeamsButton = false,
  teamsUrl,
  size = 'medium',
  showPosition = true,
  teamsIconSize = 'medium',
  onClick,
  sx = {},
  isLink = true,
  withAvatar,
  ...restProps
}) => {
  const { getEmployeeNameDataById } = useEmployeeNamesService();
  const { getSingleEntityUrl } = useEmployeeUrl();
  const { getThumbnails } = useArtifactInfoService();
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [personData, setPersonData] = useState<PersonTileData | null>(person || null);
  const [loading, setLoading] = useState(!!employeeId && !person);

  useEffect(() => {
    if (employeeId && !person) {
      setLoading(true);
      getEmployeeNameDataById(employeeId)
        .then((data: any) => {
          setPersonData(data);
          setLoading(false);
          const photoId = data?.mainPhotoId || data?.employeePhotoId;
          if (photoId) {
            getThumbnails(photoId).then(thumbnails => {
              if (thumbnails[CROP_SIZES.SIZE_100]) {
                setThumbnailUrl(thumbnails[CROP_SIZES.SIZE_100]);
              }
            });
          }
        })
        .catch(error => {
          setLoading(false);
        });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (personData?.employeePhotoId || personData?.mainPhotoId) {
      const photoId = personData.employeePhotoId || personData.mainPhotoId;
      getThumbnails(photoId!).then(thumbnails => {
        if (thumbnails && thumbnails[CROP_SIZES.SIZE_100]) {
          setThumbnailUrl(thumbnails[CROP_SIZES.SIZE_100]);
        }
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!employeeId && !person) {
    return null;
  }

  const handleTeamsClick = () => {
    if (teamsUrl) {
      window.open(teamsUrl, '_blank');
    } else if (employeeId) {
      const teamsLink = generateLinkByEmail(employeeId);
      window.open(teamsLink, '_blank');
    }
  };

  if (loading || !personData) {
    return (
      <PersonTileUI
        displayName="Loading..."
        initials=""
        position={title}
        size={size}
        showPosition={showPosition}
        showTeamsButton={showTeamsButton}
        teamsIconSize={teamsIconSize}
        onTeamsClick={showTeamsButton ? handleTeamsClick : undefined}
        onClick={onClick}
        sx={sx}
        isLink={isLink}
        loading
        {...restProps}
      />
    );
  }

  const getDisplayName = () => {
    if (personData.nameEn) {
      const { firstName, lastName } = personData.nameEn;
      return `${firstName || ''} ${lastName || ''}`.trim();
    }
    return (
      personData.name ||
      personData.fullName ||
      `${personData.firstName || ''} ${personData.lastName || ''}`.trim() ||
      employeeId ||
      ''
    );
  };

  const getInitials = () => {
    if (personData.nameEn) {
      const { firstName, lastName } = personData.nameEn;
      const firstInitial = firstName ? firstName.charAt(0) : '';
      const lastInitial = lastName ? lastName.charAt(0) : '';
      return `${firstInitial}${lastInitial}`.toUpperCase();
    }
    const displayName = getDisplayName();
    return displayName
      .split(' ')
      .map(n => n.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const displayName = getDisplayName();
  const initials = getInitials();
  const avatarSrc = thumbnailUrl || personData.avatar;
  const linkUrl = isLink ? getSingleEntityUrl(employeeId) : undefined;

  return (
    <PersonTileUI
      displayName={displayName}
      initials={initials}
      avatarSrc={avatarSrc}
      position={title || role || personData.position}
      size={size}
      showPosition={showPosition}
      showTeamsButton={showTeamsButton}
      teamsIconSize={teamsIconSize}
      onTeamsClick={showTeamsButton ? handleTeamsClick : undefined}
      onClick={onClick}
      sx={sx}
      isLink={isLink}
      linkUrl={linkUrl}
      withAvatar={withAvatar}
      {...restProps}
    />
  );
};

export default React.memo(EmployeePersonTile);
